// Notify host — RON-5469. Prototype region: `#bd-notify`.
//
// The contract is unusually specific here, because an earlier audit (A15) found a field being
// accepted and silently discarded. Three rules shape this component:
//
//   1. `channel` is `email` OR `push`, and EXACTLY ONE IS SENT. "The tabs are not decoration."
//   2. `subject` applies to EMAIL ONLY. Sending one with a push is a 422, not a silent drop —
//      a push is a title and a line of text with nowhere to put a subject.
//   3. An empty subject is not sent at all: the template's own subject reads correctly on its
//      own, and "a blank subject line is worse than a generic one".
//
// So the subject field is not merely hidden on the push tab — it is not sent, which is what
// actually matters.

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

const MESSAGE_MAX = 500;
const SUBJECT_MAX = 150;

const CHANNEL = { EMAIL: "email", PUSH: "push" };

const ReceptionNotifyModal = ({ row, isBusy, onSend, onClose }) => {
  const { t } = useTranslation("common");
  const [channel, setChannel] = useState(CHANNEL.EMAIL);
  const [message, setMessage] = useState("");
  const [subject, setSubject] = useState("");

  // A fresh dialog per visitor. Without this, the message typed for the last arrival is still in
  // the box for the next one — and at a front desk it would get sent.
  useEffect(() => {
    setChannel(CHANNEL.EMAIL);
    setMessage("");
    setSubject("");
  }, [row?.id]);

  if (!row) return null;

  const isPush = channel === CHANNEL.PUSH;

  const send = () =>
    onSend?.({
      row,
      channel,
      // Empty strings are dropped rather than sent: the template reads correctly without either,
      // and a blank subject line is worse than a generic one.
      message: message.trim() || undefined,
      // NEVER sent with a push — the server answers 422, and rightly.
      subject: isPush ? undefined : subject.trim() || undefined,
    });

  return (
    <div className="reception-modal__backdrop" role="presentation" onClick={onClose}>
      <div
        className="reception-modal"
        role="dialog"
        aria-modal="true"
        aria-label={t("react_reception_notify_host")}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="reception-modal__head">
          <h2>{t("react_reception_notify_host")}</h2>
          <button
            type="button"
            className="reception-modal__x"
            onClick={onClose}
            aria-label={t("react_reception_close")}
          >
            ✕
          </button>
        </div>

        <p className="reception-modal__sub">
          {t("react_reception_notify_sub")
            .replace("%visitor", row.name)
            .replace("%host", row.host || "—")}
        </p>

        <div className="reception-modal__body">
          <div className="reception-tabs reception-tabs--modal" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={!isPush}
              className={`reception-tab${!isPush ? " reception-tab--active" : ""}`}
              onClick={() => setChannel(CHANNEL.EMAIL)}
            >
              {t("react_reception_channel_email")}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={isPush}
              className={`reception-tab${isPush ? " reception-tab--active" : ""}`}
              onClick={() => setChannel(CHANNEL.PUSH)}
            >
              {t("react_reception_channel_push")}
            </button>
          </div>

          {/* Only on the email tab. A push has nowhere to put a subject, and the field's absence
              is what stops one being typed and then silently dropped. */}
          {!isPush && (
            <label className="reception-field">
              <span className="reception-field__label">{t("react_reception_subject")}</span>
              <input
                type="text"
                className="reception-field__input"
                value={subject}
                maxLength={SUBJECT_MAX}
                onChange={(event) => setSubject(event.target.value)}
                placeholder={t("react_reception_subject_placeholder")}
              />
              <span className="reception-field__help">
                {t("react_reception_subject_help")}
              </span>
            </label>
          )}

          <label className="reception-field">
            <span className="reception-field__label">{t("react_reception_message")}</span>
            <textarea
              className="reception-field__input reception-field__input--area"
              value={message}
              maxLength={MESSAGE_MAX}
              rows={4}
              onChange={(event) => setMessage(event.target.value)}
              placeholder={t("react_reception_message_placeholder")}
            />
            <span className="reception-field__help">
              {message.length} / {MESSAGE_MAX}
            </span>
          </label>

          {isPush && (
            <p className="reception-modal__note">{t("react_reception_push_note")}</p>
          )}
        </div>

        <div className="reception-modal__foot">
          <button
            type="button"
            className="reception-btn reception-btn--ghost"
            onClick={onClose}
          >
            {t("cancel")}
          </button>
          <button type="button" className="reception-btn" disabled={isBusy} onClick={send}>
            {/* The button names the channel it will use, so one press is never a surprise. */}
            {t(isPush ? "react_reception_send_push" : "react_reception_send_email")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReceptionNotifyModal;
